import { call, put, takeEvery, type SagaReturnType } from 'redux-saga/effects'
import {
    createStationCleanupJobs,
    updateStationCleanupJobs,
    deleteStationCleanupJobs,
    getStationCleanupJobs,
    getStations,
    setStationCleanupJobs,
    setStations,
} from 'src/redux/stationCleanupJob'
import * as api from 'src/api'
import { showNotification } from 'src/components/parts/notifications/notifications'

type CreateStationCleanupJobsRes = SagaReturnType<typeof api.createStationCleanupJobs>

export function* createStationCleanupJobsSaga(action: ReturnType<typeof createStationCleanupJobs>) {
    try {
        const res: CreateStationCleanupJobsRes | Error = yield call(
            api.createStationCleanupJobs,
            action.payload.stationIds,
            action.payload.stationCleanupJob,
        )

        if (res instanceof Error) {
            throw res
        }

        switch (res.successfulCount) {
            case action.payload.stationIds.length:
                yield call(showNotification, 'success', 'Successfully created parking cleanups')
                break
            case 0:
                yield call(showNotification, 'error', 'Failed to create parking cleanups')
                break
            default:
                yield call(showNotification, 'warn', 'Only some parking cleanups were successfully created')
                break
        }

        yield put(getStationCleanupJobs(action.payload.zoneId))
        yield put(setStations([]))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type UpdateStationCleanupJobsRes = SagaReturnType<typeof api.updateStationCleanupJobs>

export function* updateStationCleanupJobsSaga(action: ReturnType<typeof updateStationCleanupJobs>) {
    try {
        const res: UpdateStationCleanupJobsRes | Error = yield call(
            api.updateStationCleanupJobs,
            action.payload.ids,
            action.payload.stationCleanupJob,
        )

        if (res instanceof Error) {
            throw res
        }

        switch (res.successfulCount) {
            case action.payload.ids.length:
                yield call(showNotification, 'success', 'Successfully updated parking cleanups')
                break
            case 0:
                yield call(showNotification, 'error', 'Failed to update parking cleanups')
                break
            default:
                yield call(showNotification, 'warn', 'Only some parking cleanups were successfully updated')
                break
        }

        yield put(getStationCleanupJobs(action.payload.zoneId))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type DeleteStationCleanupJobsRes = SagaReturnType<typeof api.deleteStationCleanupJobs>

export function* deleteStationCleanupJobsSaga(action: ReturnType<typeof deleteStationCleanupJobs>) {
    try {
        const res: DeleteStationCleanupJobsRes | Error = yield call(api.deleteStationCleanupJobs, action.payload.ids)

        if (res instanceof Error) {
            throw res
        }

        switch (res.successfulCount) {
            case action.payload.ids.length:
                yield call(showNotification, 'success', 'Successfully deleted parking cleanups')
                break
            case 0:
                yield call(showNotification, 'error', 'Failed to delete parking cleanups')
                break
            default:
                yield call(showNotification, 'warn', 'Only some parking cleanups were successfully deleted')
                break
        }

        yield put(getStationCleanupJobs(action.payload.zoneId))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type GetStationCleanupJobsByZoneIdRes = SagaReturnType<typeof api.getStationCleanupJobsByZoneId>

export function* getStationCleanupJobsSaga(action: ReturnType<typeof getStationCleanupJobs>) {
    try {
        const res: GetStationCleanupJobsByZoneIdRes | Error = yield call(
            api.getStationCleanupJobsByZoneId,
            action.payload,
        )

        if (res instanceof Error) {
            throw res
        }

        yield put(setStationCleanupJobs(res))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

type GetStationsByIdsRes = SagaReturnType<typeof api.getStationsByIds>
export function* getStationsSaga(action: ReturnType<typeof getStations>) {
    try {
        const res: GetStationsByIdsRes | Error = yield call(
            api.getStationsByIds,
            action.payload.zoneId,
            action.payload.stationIds,
        )

        if (res instanceof Error) {
            throw res
        }

        yield put(setStations(res))
    } catch (e) {
        yield call(showNotification, 'error', (e as Error).message)
    }
}

export default function* watcher() {
    yield takeEvery(createStationCleanupJobs.toString(), createStationCleanupJobsSaga)
    yield takeEvery(updateStationCleanupJobs.toString(), updateStationCleanupJobsSaga)
    yield takeEvery(deleteStationCleanupJobs.toString(), deleteStationCleanupJobsSaga)
    yield takeEvery(getStationCleanupJobs.toString(), getStationCleanupJobsSaga)
    yield takeEvery(getStations.toString(), getStationsSaga)
}
